import { Device } from '@capacitor/device'
import { isAfter, isBefore } from 'date-fns'
import { useField } from 'formik'
import { useEffect, useState } from 'react'
import styled, { useTheme } from 'styled-components'
import { useMediaQuery } from 'usehooks-ts'

import { Checkbox } from '@/@bothrs-ui/web/components/forms/checkbox/Checkbox'
import { Input } from '@/@bothrs-ui/web/components/forms/input'
import { Flex, Margin } from '@/@bothrs-ui/web/components/layout'
import { breakpoints } from '@/config/layout'
import { formatLocale } from '@/modules/shared/helpers/locale'

import type { ChangeEvent, FocusEvent } from 'react'

type FilterTimeInputProps = {
  name: string
  label: string
  id: string
  checked: boolean
  startDate?: string
  endDate?: string
  disabled?: boolean
}

export const FilterTimeInput = ({
  name,
  label,
  id,
  checked,
  startDate,
  endDate,
  disabled = false,
}: FilterTimeInputProps) => {
  const theme = useTheme()
  const startTimeHelpers = useField(`${name}.startDate`)[2]
  const endTimeHelpers = useField(`${name}.endDate`)[2]
  const isMobile = useMediaQuery(breakpoints.toS)

  const [isLongTimeFormat, setIsLongTimeFormat] = useState(false)

  // Effects
  // ------------------------------------------------------------------------- /
  useEffect(() => {
    const asyncEffect = async () => {
      const tagObject = await Device.getLanguageTag()
      setIsLongTimeFormat(tagObject.value === 'en')
    }
    asyncEffect()
  }, [])

  // Helper methods
  // ------------------------------------------------------------------------- /
  const createDateFromTime = (time: string) => {
    const [hours, minutes] = time.split(':')
    return new Date(1990, 1, 1, Number.parseInt(hours), Number.parseInt(minutes))
  }

  // Interaction methods
  // ------------------------------------------------------------------------- /
  const handleStartTimeBlur = (event: FocusEvent<HTMLInputElement>) => {
    const target = event.target.value

    if (endDate) {
      const startTimeDate = createDateFromTime(target)
      const endTimeDate = createDateFromTime(endDate)
      if (isAfter(startTimeDate, endTimeDate)) {
        startTimeHelpers.setValue(endDate)
      }
    }
  }

  const handleStartChange = (event: ChangeEvent<HTMLInputElement>) => {
    const target = event.target.value
    const date = createDateFromTime(target)
    startTimeHelpers.setValue(formatLocale(date, 'HH:mm'))
  }

  const handleEndTimeBlur = (event: FocusEvent<HTMLInputElement>) => {
    const target = event.target.value

    if (startDate) {
      const startTimeDate = createDateFromTime(startDate)
      const endTimeDate = createDateFromTime(target)
      if (isBefore(endTimeDate, startTimeDate)) {
        endTimeHelpers.setValue(startDate)
      }
    }
  }

  return (
    <StTimeInputContainer justifyContent={'space-between'} alignItems={'center'} flexWrap={'wrap'}>
      <Checkbox
        type='checkbox'
        name={`${name}.checked`}
        label={isLongTimeFormat ? label?.slice(0, 2) : label}
        id={id}
        checked={checked}
        disabled={disabled}
      />

      <Flex>
        <Input
          type={'time'}
          name={`${name}.startDate`}
          value={startDate || '00:00'}
          onBlur={handleStartTimeBlur}
          onChange={handleStartChange}
          disabled={!checked}
          style={{
            width: isLongTimeFormat ? '120px' : isMobile ? '110px' : '80px',
          }}
          noMargin
        />
        <Margin mr={theme.UI.Spacing.Space2} />
        <Input
          type={'time'}
          name={`${name}.endDate`}
          value={endDate || '23:59'}
          onBlur={handleEndTimeBlur}
          disabled={!checked}
          style={{
            width: isLongTimeFormat ? '120px' : isMobile ? '110px' : '80px',
          }}
          noMargin
        />
      </Flex>
    </StTimeInputContainer>
  )
}

const StTimeInputContainer = styled(Flex)`
  align-items: center !important;
  & > div:first-child {
    align-items: center !important;
    & > div:first-child {
      margin-top: 0 !important;
    }
  }
`
